import React, { useState, useEffect, useRef } from 'react';
import { Invoice, HeaderEdits, CreditCard, PAYMENT_METHODS } from '../types';
import { searchVendors, VendorSearchResult } from '../api';
import { applyPaymentMethodToHeaderEdits } from '../utils/headerEdits';
import { useIsMobile } from '../hooks/useIsMobile';

interface Props {
  invoice: Invoice;
  headerEdits: HeaderEdits;
  creditCards: CreditCard[];
  onHeaderEditsChange: (edits: HeaderEdits) => void;
  onEditingChange?: (editing: boolean) => void;  // notifies parent when header edit mode opens/closes
  readOnly?: boolean;
}

function InvoiceHeader({ invoice, headerEdits, creditCards, onHeaderEditsChange, onEditingChange, readOnly }: Props) {
  const isMobile = useIsMobile();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<HeaderEdits>(headerEdits);
  const [amountInput, setAmountInput] = useState<string | null>(null);

  // Vendor search state
  const [vendorEditing, setVendorEditing] = useState(false); // true = user clicked "Change", show input regardless of query
  const [vendorQuery, setVendorQuery] = useState('');
  const [vendorResults, setVendorResults] = useState<VendorSearchResult[]>([]);
  const [vendorSearching, setVendorSearching] = useState(false);
  const [vendorDropdownOpen, setVendorDropdownOpen] = useState(false);
  const vendorSearchRef = useRef<HTMLDivElement>(null);

  // Debounced vendor search
  useEffect(() => {
    if (!vendorQuery.trim()) { setVendorResults([]); setVendorDropdownOpen(false); return; }
    const timer = setTimeout(async () => {
      setVendorSearching(true);
      try {
        const results = await searchVendors(vendorQuery);
        setVendorResults(results);
        setVendorDropdownOpen(results.length > 0);
      } finally {
        setVendorSearching(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [vendorQuery]);

  // Close vendor dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (vendorSearchRef.current && !vendorSearchRef.current.contains(e.target as Node)) {
        setVendorDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const resetVendorSearch = () => { setVendorEditing(false); setVendorQuery(''); setVendorResults([]); setVendorDropdownOpen(false); };

  const openEdit = () => { setDraft({ ...headerEdits }); setAmountInput(null); resetVendorSearch(); setEditing(true); onEditingChange?.(true); };
  const cancelEdit = () => { setDraft({ ...headerEdits }); setAmountInput(null); resetVendorSearch(); setEditing(false); onEditingChange?.(false); };

  const saveEdit = () => {
    onHeaderEditsChange({
      ...draft,
      credit_card_vendor_number: draft.payment_method_id === 2 ? draft.credit_card_vendor_number : null,
    });
    setAmountInput(null);
    setEditing(false);
    onEditingChange?.(false);
  };

  const handleAmountChange = (value: string) => setAmountInput(value.replace(/[^0-9.]/g, ''));
  const handleAmountBlur = () => {
    if (amountInput !== null) {
      setDraft(d => ({ ...d, invoice_amount: parseFloat(amountInput) || 0 }));
      setAmountInput(null);
    }
  };

  const amountDisplay = amountInput !== null ? amountInput : draft.invoice_amount.toFixed(2);

  const amountChanged        = headerEdits.invoice_amount !== invoice.invoice_amount;
  const idChanged            = headerEdits.invoice_date !== invoice.invoice_date;
  const invoiceNumChanged    = headerEdits.invoice_number !== invoice.invoice_number;
  const pmChanged            = headerEdits.payment_method_id !== invoice.payment_method_id;
  const ccChanged            = headerEdits.credit_card_vendor_number !== invoice.credit_card_vendor_number;
  const pdChanged            = headerEdits.posting_date !== (invoice.posting_date || invoice.invoice_date);
  const vendorChanged        = headerEdits.vendor_number !== invoice.vendor_number;
  const hasHeaderChanges = amountChanged || idChanged || invoiceNumChanged || pmChanged || ccChanged || pdChanged || vendorChanged;

  const paymentLabel = (id: number) => PAYMENT_METHODS.find(p => p.id === id)?.label ?? `Method ${id}`;
  const ccLabel = (vendorNumber: number | null): string => {
    if (vendorNumber == null) return '—';
    const card = creditCards.find(c => c.cc_vendor_number === vendorNumber);
    return card ? `${card.description} (#${card.cc_vendor_number})` : `Vendor #${vendorNumber}`;
  };

  const statusColors: Record<string, { bg: string; color: string }> = {
    APPROVED: { bg: '#e8f5e9', color: '#2d7a2d' },
    REJECTED: { bg: '#fff0ee', color: '#E33126' },
    CORRECTED: { bg: '#e8f0ff', color: '#00386C' },
    PENDING:   { bg: '#fff8e1', color: '#b7860b' },
  };
  const sc = statusColors[invoice.status] ?? { bg: '#f0f4f8', color: '#4a5568' };

  const labelStyle: React.CSSProperties = {
    margin: '0 0 3px 0', fontSize: '11px', color: '#718096',
    textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600,
  };
  const valueStyle: React.CSSProperties = {
    margin: 0, fontSize: '15px', fontWeight: 700, color: '#1a202c',
  };
  const changedValue: React.CSSProperties = { ...valueStyle, color: '#00386C' };
  const subStyle: React.CSSProperties = { margin: '2px 0 0 0', fontSize: '12px', color: '#718096' };
  const strikeStyle: React.CSSProperties = { ...subStyle, textDecoration: 'line-through', color: '#a0aec0' };

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '8px 10px', border: '1px solid #cbd5e0',
    borderRadius: '4px', fontSize: '14px', fontFamily: 'inherit',
    boxSizing: 'border-box', color: '#1a202c', backgroundColor: 'white', outline: 'none',
  };
  const editLabel: React.CSSProperties = {
    display: 'block', fontSize: '12px', color: '#4a5568',
    marginBottom: '5px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.3px',
  };

  return (
    <div style={{
      backgroundColor: 'white', borderRadius: '8px', padding: '24px',
      marginBottom: '16px', boxShadow: '0 1px 4px rgba(0,56,108,0.08)',
    }}>

      {/* Title bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <h2 style={{
            margin: 0, fontSize: '14px', color: '#00386C',
            textTransform: 'uppercase', letterSpacing: '0.6px', fontWeight: 700,
          }}>
            Invoice Details
          </h2>
          {hasHeaderChanges && (
            <span style={{
              fontSize: '10px', fontWeight: 700, backgroundColor: '#fffbea',
              color: '#b7770d', padding: '2px 8px', borderRadius: '10px',
              letterSpacing: '0.4px', border: '1px solid #f6d860',
            }}>
              MODIFIED
            </span>
          )}
        </div>

        {readOnly ? null : !editing ? (
          <button onClick={openEdit} style={{
            padding: '6px 16px', fontSize: '13px', fontWeight: 700,
            fontFamily: 'inherit', backgroundColor: '#00386C',
            color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: '6px',
          }}>
            <span style={{ fontSize: 13 }}>✎</span> Edit Header
          </button>
        ) : (
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={saveEdit} style={{
              padding: '6px 16px', fontSize: '13px', fontWeight: 700,
              fontFamily: 'inherit', backgroundColor: '#2d7a2d',
              color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer',
            }}>
              ✓ Save
            </button>
            <button onClick={cancelEdit} style={{
              padding: '6px 14px', fontSize: '13px', fontFamily: 'inherit',
              backgroundColor: 'white', color: '#4a5568',
              border: '1px solid #cbd5e0', borderRadius: '4px', cursor: 'pointer',
            }}>
              Cancel
            </button>
          </div>
        )}
      </div>

      {/* Read-only grid */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '20px' }}>

        <div>
          <p style={labelStyle}>Vendor</p>
          <p style={{ ...(vendorChanged ? changedValue : valueStyle), fontSize: '17px' }}>
            {headerEdits.vendor_name || invoice.vendor_name}
          </p>
          <p style={vendorChanged ? { ...subStyle, color: '#00386C', fontWeight: 700 } : subStyle}>
            Vendor #{headerEdits.vendor_number ?? invoice.vendor_number}
          </p>
          {vendorChanged && invoice.vendor_number != null && (
            <p style={strikeStyle}>Original: {invoice.vendor_name} #{invoice.vendor_number}</p>
          )}
        </div>

        <div>
          <p style={labelStyle}>Invoice</p>
          <p style={{ ...(invoiceNumChanged ? changedValue : valueStyle), fontSize: '17px' }}>
            {headerEdits.invoice_number}
          </p>
          {invoiceNumChanged && <p style={strikeStyle}>Original: {invoice.invoice_number}</p>}
          <p style={idChanged ? { ...subStyle, color: '#00386C', fontWeight: 700 } : subStyle}>
            Invoice Date: {headerEdits.invoice_date}
          </p>
          {idChanged && <p style={strikeStyle}>Original: {invoice.invoice_date}</p>}
        </div>

        <div>
          <p style={labelStyle}>Amount</p>
          <p style={{ ...( amountChanged ? changedValue : valueStyle), fontSize: '22px' }}>
            ${headerEdits.invoice_amount.toFixed(2)}
          </p>
          {amountChanged && <p style={strikeStyle}>Original: ${invoice.invoice_amount.toFixed(2)}</p>}
        </div>

        <div>
          <p style={labelStyle}>Posting Date</p>
          <p style={pdChanged ? changedValue : valueStyle}>{headerEdits.posting_date}</p>
          {pdChanged && <p style={strikeStyle}>Invoice date: {invoice.invoice_date}</p>}
        </div>

        <div>
          <p style={labelStyle}>Payment Method</p>
          <p style={pmChanged ? changedValue : valueStyle}>{paymentLabel(headerEdits.payment_method_id)}</p>
          {pmChanged && invoice.payment_method_id != null && <p style={strikeStyle}>Original: {paymentLabel(invoice.payment_method_id)}</p>}
        </div>

        {headerEdits.payment_method_id === 2 && (
          <div>
            <p style={labelStyle}>Credit Card</p>
            <p style={{ ...(ccChanged ? changedValue : valueStyle), fontSize: '14px' }}>
              {ccLabel(headerEdits.credit_card_vendor_number)}
            </p>
            {headerEdits.credit_card_vendor_number == null && (
              <p style={{ ...subStyle, color: '#E33126', fontWeight: 700 }}>⚠ No card selected</p>
            )}
            {ccChanged && invoice.credit_card_vendor_number != null && (
              <p style={strikeStyle}>Original: {ccLabel(invoice.credit_card_vendor_number)}</p>
            )}
          </div>
        )}

        <div>
          <p style={labelStyle}>Job</p>
          <p style={{ ...valueStyle, fontSize: '15px' }}>{invoice.job_name ?? '—'}</p>
          <p style={subStyle}>{invoice.job_number ? `Job #${invoice.job_number}` : 'No job assigned'}</p>
        </div>

        <div>
          <p style={labelStyle}>Status</p>
          <span style={{
            display: 'inline-block', padding: '3px 12px', borderRadius: '12px',
            fontSize: '12px', fontWeight: 700,
            backgroundColor: sc.bg, color: sc.color, letterSpacing: '0.3px',
          }}>
            {invoice.status}
          </span>
        </div>

        <div style={{ gridColumn: '1 / -1' }}>
          <p style={labelStyle}>Source</p>
          <p style={{ margin: 0, fontSize: '13px', color: '#718096' }}>
            {invoice.source_filename || invoice.source_email_subject || '—'}
          </p>
        </div>

      </div>

      {/* Inline edit panel */}
      {editing && (
        <div style={{
          marginTop: '24px', padding: '20px', backgroundColor: '#f0f4f8',
          borderRadius: '6px', border: '1px solid #cbd5e0',
        }}>
          <p style={{
            margin: '0 0 16px 0', fontSize: '12px', fontWeight: 700,
            color: '#00386C', letterSpacing: '0.5px', textTransform: 'uppercase',
          }}>
            Edit Header Fields
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px' }}>

            {/* Vendor */}
            <div ref={vendorSearchRef}>
              <label style={editLabel}>Vendor</label>

              {/* Chip: vendor is selected and user is not actively searching */}
              {draft.vendor_number != null && !vendorEditing ? (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '10px',
                  backgroundColor: '#f0fff4', border: '1px solid #68d391',
                  borderRadius: '4px', padding: '7px 12px',
                }}>
                  <span style={{ fontSize: '13px', color: '#276749', fontWeight: 700, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    ✓ #{draft.vendor_number} — {draft.vendor_name}
                  </span>
                  <button
                    onClick={() => { setVendorEditing(true); setVendorQuery(''); }}
                    style={{
                      fontSize: '12px', color: '#718096', background: 'none',
                      border: '1px solid #cbd5e0', borderRadius: '3px',
                      cursor: 'pointer', padding: '2px 8px', fontFamily: 'inherit', flexShrink: 0,
                    }}
                  >
                    Change
                  </button>
                </div>
              ) : (
                /* Search input + dropdown */
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    value={vendorQuery}
                    onChange={e => setVendorQuery(e.target.value)}
                    placeholder="Type vendor name or number…"
                    autoComplete="off"
                    style={inputStyle}
                  />
                  {vendorSearching && (
                    <span style={{
                      position: 'absolute', right: '10px', top: '50%',
                      transform: 'translateY(-50%)', fontSize: '12px', color: '#718096',
                    }}>⏳</span>
                  )}
                  {vendorDropdownOpen && vendorResults.length > 0 && (
                    <div style={{
                      position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200,
                      backgroundColor: 'white', border: '1px solid #cbd5e0', borderRadius: '4px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.12)', maxHeight: '220px',
                      overflowY: 'auto', marginTop: '2px',
                    }}>
                      {vendorResults.map(v => (
                        <button
                          key={v.vendor_number}
                          onMouseDown={e => {
                            e.preventDefault();
                            setDraft(d => ({ ...d, vendor_number: v.vendor_number, vendor_name: v.vendor_name }));
                            resetVendorSearch();
                          }}
                          style={{
                            display: 'block', width: '100%', textAlign: 'left',
                            padding: '8px 12px', border: 'none', background: 'white',
                            cursor: 'pointer', fontSize: '13px',
                            borderBottom: '1px solid #f0f4f8',
                          }}
                          onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f0f4ff')}
                          onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'white')}
                        >
                          <span style={{ fontWeight: 700, color: '#00386C' }}>#{v.vendor_number}</span>
                          <span style={{ marginLeft: '8px' }}>{v.vendor_name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: '#718096' }}>
                Original: {invoice.vendor_name}{invoice.vendor_number != null ? ` (#${invoice.vendor_number})` : ''}
              </p>
            </div>

            {/* Invoice Number — sits in col 2, next to Vendor */}
            <div>
              <label style={editLabel}>Invoice Number</label>
              <input
                type="text"
                value={draft.invoice_number}
                onChange={e => setDraft(d => ({ ...d, invoice_number: e.target.value }))}
                placeholder="Invoice / Credit #"
                style={inputStyle}
              />
              <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: '#718096' }}>
                Original: {invoice.invoice_number}
              </p>
            </div>

            {/* Invoice Amount */}
            <div>
              <label style={editLabel}>Invoice Amount</label>
              <div style={{
                display: 'flex', alignItems: 'center', border: '1px solid #cbd5e0',
                borderRadius: '4px', overflow: 'hidden', backgroundColor: 'white',
              }}>
                <span style={{
                  padding: '8px 8px 8px 10px', backgroundColor: '#f0f4f8',
                  color: '#718096', fontSize: '14px', borderRight: '1px solid #cbd5e0', flexShrink: 0,
                }}>$</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={amountDisplay}
                  onChange={e => handleAmountChange(e.target.value)}
                  onBlur={handleAmountBlur}
                  style={{
                    width: '100%', padding: '8px 10px', border: 'none',
                    fontSize: '14px', fontFamily: 'inherit', outline: 'none',
                    textAlign: 'right', color: '#1a202c', backgroundColor: 'white',
                  }}
                />
              </div>
              <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: '#718096' }}>
                Original: ${invoice.invoice_amount.toFixed(2)}
              </p>
            </div>

            {/* Invoice Date */}
            <div>
              <label style={editLabel}>Invoice Date</label>
              <input
                type="date"
                value={draft.invoice_date}
                onChange={e => setDraft(d => ({ ...d, invoice_date: e.target.value }))}
                style={inputStyle}
              />
              <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: '#718096' }}>
                Original: {invoice.invoice_date}
              </p>
            </div>

            {/* Posting Date */}
            <div>
              <label style={editLabel}>Posting Date</label>
              <input
                type="date"
                value={draft.posting_date}
                onChange={e => setDraft(d => ({ ...d, posting_date: e.target.value }))}
                style={inputStyle}
              />
              <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: '#718096' }}>
                Defaults to invoice date if not changed
              </p>
            </div>

            {/* Payment Method */}
            <div>
              <label style={editLabel}>Payment Method</label>
              <select
                value={draft.payment_method_id}
                onChange={e => {
                  // No silent GLOBAL-default card: switching to Credit Card keeps only a
                  // card that is already on the draft; otherwise the reviewer must pick one.
                  const newMethod = parseInt(e.target.value);
                  setDraft(d => applyPaymentMethodToHeaderEdits(d, newMethod));
                }}
                style={inputStyle}
              >
                {PAYMENT_METHODS.map(pm => <option key={pm.id} value={pm.id}>{pm.label}</option>)}
              </select>
            </div>

            {/* Credit Card */}
            {draft.payment_method_id === 2 && (
              <div>
                <label style={editLabel}>Credit Card</label>
                <select
                  value={draft.credit_card_vendor_number ?? ''}
                  onChange={e => setDraft(d => ({
                    ...d,
                    credit_card_vendor_number: e.target.value ? parseInt(e.target.value) : null,
                  }))}
                  style={inputStyle}
                >
                  <option value="">— Select card —</option>
                  {creditCards.map(card => (
                    <option key={card.cc_vendor_number} value={card.cc_vendor_number}>
                      {card.description} #{card.cc_vendor_number}{card.is_default ? ' (default)' : ''}
                    </option>
                  ))}
                </select>
                <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: '#718096' }}>
                  Routes payment to WinTeam credit card reconciliation
                </p>
              </div>
            )}

          </div>
        </div>
      )}
    </div>
  );
}

export default InvoiceHeader;
